import { Box, IconButton, TablePagination, TextField, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getClinicianPayouts,
  getClinicianProfile,
  createPayout,
  processPayout,
} from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";

export const PayoutsList = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(5);
  const [page, setPage] = useState<number>(0);
  const [openPayoutDialog, setOpenPayoutDialog] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: "",
    paymentMode: "BANK_TRANSFER",
    paymentDetails: "",
  });
  const queryClient = useQueryClient();

  const { data: clinicianData } = useQuery(
    ["clinician-profile"],
    getClinicianProfile
  );

  const { data, isLoading } = useQuery(
    [
      "payouts",
      {
        clinicianId: clinicianData?.data?.id,
        limit: limit,
        offset: limit * page,
      },
    ],
    getClinicianPayouts,
    {
      enabled: Boolean(clinicianData?.data?.id),
    }
  );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Create payout mutation
  const { mutate: createPayoutMutation, isLoading: isCreating } = useMutation(
    createPayout,
    {
      onSuccess: () => {
        toast.success("Payout request created successfully!");
        queryClient.invalidateQueries("payouts");
        setOpenPayoutDialog(false);
        setPayoutForm({ amount: "", paymentMode: "BANK_TRANSFER", paymentDetails: "" });
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Failed to create payout");
      },
    }
  );

  // Process payout mutation
  const { mutate: processPayoutMutation } = useMutation(
    processPayout,
    {
      onSuccess: () => {
        toast.success("Payout processed successfully!");
        queryClient.invalidateQueries("payouts");
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Failed to process payout");
      },
    }
  );

  const handleCreatePayout = () => {
    if (!payoutForm.amount || !payoutForm.paymentMode) {
      toast.error("Please fill all required fields");
      return;
    }
    createPayoutMutation({
      clinicianId: clinicianData?.data?.id,
      amount: parseFloat(payoutForm.amount),
      paymentMode: payoutForm.paymentMode,
      paymentDetails: payoutForm.paymentDetails,
    });
  };

  const handleProcessPayout = (payoutId: string) => {
    if (window.confirm("Are you sure you want to process this payout?")) {
      processPayoutMutation(parseInt(payoutId));
    }
  };

  const columns = [
    {
      title: "Payment Reference ID",
      dataIndex: "paymentReferenceId",
      key: "paymentReferenceId",
    },

    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (colData: any) => {
        return moment(colData).format("DD-MM-YYYY");
      },
    },
    {
      title: "Payment",
      dataIndex: "amount",
      key: "amount",
      render: (colData: any) => {
        return `$${colData?.toFixed(2)}`;
      },
    },

    {
      title: "Payment Mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => {
        const statusColors: any = {
          PENDING: "warning",
          PROCESSING: "info",
          COMPLETED: "success",
          FAILED: "error",
        };
        return (
          <Chip
            label={colData || "PENDING"}
            color={statusColors[colData] || "default"}
            size="small"
          />
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "actions",
      render: (colData: any, row: any) => {
        if (row.status === "PENDING") {
          return (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleProcessPayout(colData)}
            >
              Process
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search by Payment Reference ID"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenPayoutDialog(true)}
        >
          Request Payout
        </Button>
      </Box>
      <Box>
        {data?.data?.count === 0 ? (
          <EmptyScreen type="Payouts" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <Table
                  headAlign={"left"}
                  tableAlign={"left"}
                  key={"payouts"}
                  dataSource={data?.data?.payouts}
                  rowKey={"id"}
                  columns={columns}
                  loading={isLoading}
                />
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={data?.data.count}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {/* Create Payout Dialog */}
      <Dialog open={openPayoutDialog} onClose={() => setOpenPayoutDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request New Payout</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Amount"
              type="number"
              value={payoutForm.amount}
              onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Payment Mode</InputLabel>
              <Select
                value={payoutForm.paymentMode}
                onChange={(e) => setPayoutForm({ ...payoutForm, paymentMode: e.target.value })}
                label="Payment Mode"
              >
                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                <MenuItem value="PAYPAL">PayPal</MenuItem>
                <MenuItem value="CHEQUE">Cheque</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Payment Details (Account/Email/etc)"
              value={payoutForm.paymentDetails}
              onChange={(e) => setPayoutForm({ ...payoutForm, paymentDetails: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayoutDialog(false)}>Cancel</Button>
          <Button onClick={handleCreatePayout} variant="contained" disabled={isCreating}>
            {isCreating ? "Creating..." : "Create Payout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
